import React, { useEffect, useState, useRef } from "react";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ThemeOverrideHandler from "../components/ThemeOverrideHandler";
import { verifyReactivationPayment } from "../api/apiFunction/billingService";

const ReactivateVerify = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    const verifyReactivation = async () => {
      // Prevent double API calls
      if (hasVerifiedRef.current) return;
      hasVerifiedRef.current = true;
      try {
        // Get session_id and subscription_id from URL
        const sessionId = searchParams.get("session_id");
        const subscriptionId =
          searchParams.get("subscription_id") ||
          localStorage.getItem("reactivation_subscription_id");

        if (!sessionId) {
          setStatus("error");
          setMessage("Missing session ID. Please try again.");
          return;
        }

        if (!subscriptionId) {
          setStatus("error");
          setMessage("Missing subscription ID. Please try again.");
          return;
        }

        // Call verify reactivation payment API
        const response = await verifyReactivationPayment(
          sessionId,
          subscriptionId
        );

        if (response?.status === 200 && response?.data?.success) {
          setStatus("success");
          setMessage(
            response.data.message || "Subscription reactivated successfully!"
          );

          // Set subscription details for display
          if (response.data.data?.subscription) {
            const sub = response.data.data.subscription;
            setSubscriptionDetails({
              plan: sub.planId?.name || sub.plan?.name || "LeebAI Pro",
              amount: sub.amount || 0,
              currency: sub.currency || "USD",
              interval: sub.interval || "monthly",
              status: sub.status || "active",
              nextBillingDate: sub.nextBillingDate || sub.currentPeriodEnd,
            });
          }

          // Update localStorage user data if needed
          const user = localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))
            : null;

          if (user && response.data.data?.subscription) {
            user.subscription = response.data.data.subscription;
            localStorage.setItem("user", JSON.stringify(user));
          }

          // Clean up localStorage
          localStorage.removeItem("reactivation_subscription_id");
          localStorage.removeItem("payment_session_id");
        } else {
          setStatus("error");
          setMessage(
            response?.data?.message ||
              response?.data?.error ||
              "Reactivation verification failed"
          );
        }
      } catch (error) {
        setStatus("error");

        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to verify reactivation payment. Please contact support.";
        setMessage(errorMsg);

        toast.error(errorMsg);
      }
    };

    verifyReactivation();
  }, []);

  const handleGoToChat = () => {
    navigate("/chat");
  };

  const handleGoToSettings = () => {
    // Navigate to chat with settings tab parameter
    navigate("/chat?tab=billing&openSettings=true");
  };

  const handleRetry = () => {
    navigate("/chat?tab=billing&openSettings=true");
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <>
      <ThemeOverrideHandler theme="light" />
      <div className="min-h-screen flex flex-col items-center justify-center bg-bg-50 px-4">
        <div className="max-w-md w-full text-center">
          {/* Status Icon */}
          <div className="mb-6">
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
                status === "loading"
                  ? "bg-blue-100 dark:bg-blue-900"
                  : status === "success"
                  ? "bg-green-100 dark:bg-green-900"
                  : "bg-red-100 dark:bg-red-900"
              }`}
            >
              {status === "loading" && (
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
              )}
              {status === "success" && (
                <CheckCircle className="w-8 h-8 text-green-600" />
              )}
              {status === "error" && (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold mb-4 text-fg-50">
            {status === "loading" && "Verifying Payment"}
            {status === "success" && "Subscription Reactivated! 🎉"}
            {status === "error" && "Reactivation Failed"}
          </h1>

          {/* Message */}
          <p className="text-fg-60 text-lg mb-8">
            {status === "loading" &&
              "Please wait while we verify your payment and reactivate your subscription..."}
            {status === "success" &&
              "Your subscription has been successfully reactivated. Welcome back! 🚀"}
            {status === "error" &&
              (message ||
                "Something went wrong. Please try again or contact support.")}
          </p>

          {/* Subscription Details */}
          {status === "success" && subscriptionDetails && (
            <div className="bg-bg-60 rounded-lg p-4 mb-8 text-left">
              <h3 className="font-medium text-fg-50 mb-3">
                Subscription Details
              </h3>
              <div className="space-y-2 text-sm text-fg-60">
                <div className="flex justify-between">
                  <span className="font-medium">Plan:</span>
                  <span className="text-fg-50 capitalize">
                    {subscriptionDetails.plan}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Amount:</span>
                  <span className="text-fg-50">
                    {subscriptionDetails.currency.toUpperCase()} $
                    {subscriptionDetails.amount.toFixed(2)}/
                    {subscriptionDetails.interval}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span>
                  <span className="text-green-600 dark:text-green-400 capitalize font-medium">
                    {subscriptionDetails.status}
                  </span>
                </div>
                {subscriptionDetails.nextBillingDate && (
                  <div className="flex justify-between">
                    <span className="font-medium">Next Billing:</span>
                    <span className="text-fg-50">
                      {new Date(
                        subscriptionDetails.nextBillingDate
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {status === "success" && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleGoToSettings}
                className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
              >
                View Billing
              </button>
              <button
                onClick={handleGoToChat}
                className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
              >
                Go to Chat
              </button>
            </div>
          )}

          {status === "error" && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleGoHome}
                className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
              >
                Back to Home
              </button>
              <button
                onClick={handleRetry}
                className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Help Text */}
          {status !== "loading" && (
            <div className="mt-8 text-sm text-fg-60">
              <p>
                Need help? Contact our{" "}
                <a
                  href="/contact"
                  className="text-ac-02 hover:text-ac-01 underline"
                >
                  support team
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReactivateVerify;
